import { useCallback, useEffect, useState } from 'react';

import useForwardGeocode from 'hooks/useForwardGeocode';
import useSearchRecents from 'hooks/useSearchRecents';

import type { UseSearchStateReturn } from './types';

// Self-contained state for the search drawer — query, expand flag, and the
// two data sources (recents + forward-geocode results). Typing a non-empty
// query auto-expands the drawer so autocomplete results are visible.
const useSearchState = (): UseSearchStateReturn => {
    const [query, setQueryRaw] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);

    const { fetchResults, results } = useForwardGeocode();
    const { addRecent, recents } = useSearchRecents();

    useEffect(() => {
        void fetchResults(query);

        if (query.trim().length > 0) {
            setIsExpanded(true);
        }
    }, [fetchResults, query]);

    const setQuery = useCallback((next: string) => {
        setQueryRaw(next);
    }, []);

    const expand = useCallback(() => setIsExpanded(true), []);
    const collapse = useCallback(() => setIsExpanded(false), []);

    return {
        collapse,
        expand,
        isExpanded,
        query,
        recents,
        recordRecent: addRecent,
        results,
        setQuery
    };
};

export default useSearchState;
